import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator, 
  TrendingUp, 
  Building, 
  PiggyBank, 
  Shield, 
  Zap,
  Target,
  Users,
  BarChart3,
  DollarSign
} from 'lucide-react';
import CanonicalURL from '../components/CanonicalURL';
import './AboutPage.css';

const AboutPage = () => {
  return (
    <>
      <CanonicalURL />
      <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-container">
          <div className="about-hero-content">
            <div className="about-logo-large">
              <Calculator className="about-logo-icon" />
            </div>
            <h1 className="about-title">About WealthCore</h1>
            <p className="about-subtitle">
              The only all-in-one wealth management platform combining tax optimization, 
              stock analysis, real estate portfolio management, and retirement planning.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-section">
        <div className="about-container">
          <div className="about-content-grid">
            <div className="about-content-text">
              <h2 className="about-section-title">Our Mission</h2>
              <p className="about-text">
                WealthCore was created to simplify financial management by bringing all 
                aspects of your financial life into one integrated platform. We believe 
                that managing your wealth shouldn't require multiple tools, spreadsheets, 
                or expensive advisors.
              </p>
              <p className="about-text">
                Our goal is to empower individuals and families to take control of their 
                financial future with powerful, affordable tools that provide real insights 
                and actionable recommendations.
              </p>
            </div>
            <div className="about-content-visual">
              <div className="about-stats-grid">
                <div className="about-stat-card">
                  <Target className="about-stat-icon" />
                  <div className="about-stat-value">All-in-One</div>
                  <div className="about-stat-label">Complete Financial Platform</div>
                </div>
                <div className="about-stat-card">
                  <Zap className="about-stat-icon" />
                  <div className="about-stat-value">Fast & Reliable</div>
                  <div className="about-stat-label">Real-Time Updates</div>
                </div>
                <div className="about-stat-card">
                  <Shield className="about-stat-icon" />
                  <div className="about-stat-value">Secure</div>
                  <div className="about-stat-label">Your Data Protected</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="about-section about-section-alt">
        <div className="about-container">
          <h2 className="about-section-title text-center">What Makes WealthCore Unique</h2>
          <div className="about-features-grid">
            <div className="about-feature-card">
              <div className="about-feature-icon">
                <Calculator className="about-icon" />
              </div>
              <h3 className="about-feature-title">Year-Round Tax Optimization</h3>
              <p className="about-feature-text">
                Unlike traditional tax software that only helps during tax season, 
                WealthCore provides year-round tax planning and optimization. Track 
                expenses, manage deductions, and optimize your tax strategy throughout 
                the entire year.
              </p>
            </div>

            <div className="about-feature-card">
              <div className="about-feature-icon">
                <TrendingUp className="about-icon" />
              </div>
              <h3 className="about-feature-title">Advanced Stock Analysis</h3>
              <p className="about-feature-text">
                Unique RSI Extended detection, Scene of Crime pattern recognition, 
                and comprehensive S&P 500 screening. Our proprietary analysis 
                methodologies help identify trading opportunities others miss.
              </p>
            </div>

            <div className="about-feature-card">
              <div className="about-feature-icon">
                <Building className="about-icon" />
              </div>
              <h3 className="about-feature-title">Real Estate Portfolio Management</h3>
              <p className="about-feature-text">
                Track rental properties, manage tenants, calculate depreciation, 
                and optimize real estate tax strategies. Everything you need to 
                manage your property investments in one place.
              </p>
            </div>

            <div className="about-feature-card">
              <div className="about-feature-icon">
                <PiggyBank className="about-icon" />
              </div>
              <h3 className="about-feature-title">Retirement Planning</h3>
              <p className="about-feature-text">
                Roth IRA planning, retirement account tracking, and comprehensive 
                retirement strategy optimization. Plan for your future with confidence.
              </p>
            </div>

            <div className="about-feature-card">
              <div className="about-feature-icon">
                <BarChart3 className="about-icon" />
              </div>
              <h3 className="about-feature-title">Integrated Dashboard</h3>
              <p className="about-feature-text">
                See your complete financial picture at a glance. All your accounts, 
                properties, investments, and tax information in one unified view.
              </p>
            </div>

            <div className="about-feature-card">
              <div className="about-feature-icon">
                <DollarSign className="about-icon" />
              </div>
              <h3 className="about-feature-title">Complete Financial Tracking</h3>
              <p className="about-feature-text">
                Track all your income, expenses, investments, and properties in one place. 
                Get a complete picture of your financial health with integrated reporting 
                and real-time updates across all your accounts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="about-section">
        <div className="about-container">
          <h2 className="about-section-title text-center">Built for Modern Financial Management</h2>
          <div className="about-tech-grid">
            <div className="about-tech-item">
              <h4 className="about-tech-title">Real-Time Data</h4>
              <p className="about-tech-text">
                Live market data, real-time tax calculations, and up-to-date 
                property valuations keep you informed.
              </p>
            </div>
            <div className="about-tech-item">
              <h4 className="about-tech-title">Secure & Private</h4>
              <p className="about-tech-text">
                Your financial data is encrypted and stored securely. We never 
                share your information with third parties.
              </p>
            </div>
            <div className="about-tech-item">
              <h4 className="about-tech-title">Always Available</h4>
              <p className="about-tech-text">
                Access your financial information anytime, anywhere. Cloud-based 
                platform works on all devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-container">
          <h2 className="about-cta-title">Ready to Take Control of Your Financial Life?</h2>
          <p className="about-cta-text">
            Join WealthCore today and start managing your entire financial portfolio 
            from one powerful platform.
          </p>
          <div className="about-cta-buttons">
            <Link to="/register" className="about-cta-button about-cta-primary">
              Get Started Free
            </Link>
            <Link to="/platform" className="about-cta-button about-cta-secondary">
              Platform Overview
            </Link>
            <Link to="/help" className="about-cta-button about-cta-secondary">
              Help & FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default AboutPage;

